// ============================================================================
// ConvertToClientModal — record a CONVERSION: a W2-hourly TCS worker is hired
// DIRECTLY by the client (hospital), coming off TCS payroll (HR, 2026-06-15).
//
// Flat $1,450 fee if the client hires them within 6 months of their TCS start
// date; none after. The fee preview here mirrors the server rule exactly; the
// SECURITY DEFINER convert_employee_to_client_hire RPC is the source of truth
// (it records the separation, the billing line, and flips the employee).
// ============================================================================
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { X as XIcon, ArrowRightLeft } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { convertEmployeeToClientHire } from './hooks/use-hr-data';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { overlayClickClose } from '../../shared/overlay-close';

const CONVERSION_FEE = 1450;

type Rec = Record<string, string | number | null>;
interface FacilityOpt {
  facility_id: string;
  facility_name: string | null;
  account_id: string | null;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

// Mirror of the server rule: hire within 6 months of the TCS start date.
function withinWindow(startISO: string, convISO: string): boolean {
  if (!startISO || !convISO) return false;
  const start = new Date(startISO + 'T00:00:00Z');
  const conv = new Date(convISO + 'T00:00:00Z');
  if (Number.isNaN(start.getTime()) || Number.isNaN(conv.getTime())) return false;
  const limit = new Date(start);
  limit.setUTCMonth(limit.getUTCMonth() + 6);
  return conv.getTime() <= limit.getTime();
}

interface Props {
  employee: Rec;
  onClose: () => void;
  onConverted: (fee: number) => void;
}

export default function ConvertToClientModal({ employee, onClose, onConverted }: Props) {
  const e = employee;
  const employeeId = String(e.employee_id ?? '');
  const name =
    [e.employee_name_first, e.employee_name_last].filter(Boolean).join(' ') || employeeId;

  const [facilities, setFacilities] = useState<FacilityOpt[]>([]);
  const [facilityId, setFacilityId] = useState<string>(String(e.primary_facility_id ?? ''));
  const [conversionDate, setConversionDate] = useState<string>(todayISO());
  const [startDate, setStartDate] = useState<string>(String(e.hire_date ?? ''));
  const [autoBill, setAutoBill] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await supabase
        .from('facilities')
        .select('facility_id, facility_name, account_id')
        .order('facility_name', { ascending: true });
      if (cancelled || res.error || !res.data) return;
      setFacilities(res.data as FacilityOpt[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const chosen = useMemo(
    () => facilities.find((f) => f.facility_id === facilityId) ?? null,
    [facilities, facilityId],
  );
  const feeApplies = useMemo(
    () => withinWindow(startDate, conversionDate),
    [startDate, conversionDate],
  );

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!facilityId) {
      setError('Select the hiring facility (the client hiring this worker).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await convertEmployeeToClientHire({
        employeeId,
        facilityId,
        accountId: chosen?.account_id ?? null,
        conversionDate,
        startDateOverride: startDate || null,
        autoBill,
      });
      onConverted(res.conversion_fee);
    } catch (err) {
      console.error('[hr] conversion failed', err);
      setError(rpcErrorMessage(err, 'Could not record this conversion.'));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Convert to client direct hire"
      {...overlayClickClose(onClose)}
    >
      <form
        onClick={(ev) => ev.stopPropagation()}
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-xl bg-tcs-overlay p-5 shadow-xl sm:rounded-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-tcs-text">
              <ArrowRightLeft className="h-4 w-4" /> Convert to client hire
            </h2>
            <p className="truncate text-xs text-tcs-text-muted">
              {name} leaves TCS payroll and is hired directly by the client.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <Field label="Hiring facility (client)">
            <select
              value={facilityId}
              onChange={(ev) => setFacilityId(ev.target.value)}
              className={inputCls}
            >
              <option value="">Select a facility…</option>
              {facilities.map((f) => (
                <option key={f.facility_id} value={f.facility_id}>
                  {f.facility_name ?? f.facility_id}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="TCS start date">
              <input
                type="date"
                value={startDate}
                onChange={(ev) => setStartDate(ev.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Conversion date">
              <input
                type="date"
                value={conversionDate}
                onChange={(ev) => setConversionDate(ev.target.value)}
                className={inputCls}
              />
            </Field>
          </div>

          <div
            className={`rounded-md border px-3 py-2 text-sm ${
              feeApplies
                ? 'border-st-warn-border bg-st-warn-bg text-st-warn-fg-strong'
                : 'border-st-ok-border bg-st-ok-bg text-st-ok-fg-strong'
            }`}
          >
            {feeApplies ? (
              <>
                <strong>$1,450 conversion fee applies</strong> — the client is hiring within
                6 months of the TCS start date.
              </>
            ) : (
              <>
                <strong>No conversion fee</strong> — the hire is more than 6 months after the
                TCS start date{startDate ? '' : ' (no start date on file)'}.
              </>
            )}
          </div>

          {feeApplies && (
            <label className="flex items-center gap-2 text-sm text-tcs-text-secondary">
              <input
                type="checkbox"
                checked={autoBill}
                onChange={(ev) => setAutoBill(ev.target.checked)}
                className="h-4 w-4"
              />
              Auto-create the ${CONVERSION_FEE.toLocaleString()} billing line for finance
            </label>
          )}
        </div>

        {error && (
          <p className="mt-3 rounded-md border border-rag-red bg-st-danger-bg p-2 text-xs text-st-danger-fg-strong">{error}</p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-tcs-border px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
          >
            {submitting ? 'Recording…' : 'Record conversion'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">{label}</span>
      {children}
    </label>
  );
}
